import React from "react";
import { ChatWidgetMenuHeader } from "./ChatWidgetItemBackButton";
import { ChatWidgetCurrent } from "../../model/chat-wiget/types/ChatWigetCurrent.enum";
import styled, { css } from "styled-components";
import { Flex } from "../Flex";
import { Spacing } from "../../theme/Spacing";
import { Rounded } from "../../theme/Rounded";
import { Shadow } from "../../theme/Shadow";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { FAHMY_BUTTON_SIZE_PX } from "./useDraggableFahmyButton";

// 28rem, matches the Container's own width/height below.
const PANEL_SIZE_PX = 448;
// Matches Spacing.m (1rem) — same gutter the existing max-width already uses.
const EDGE_GUTTER_PX = 16;
const GAP_PX = 16;

const Container = styled(Flex)<{
  $left?: number;
  $top?: number;
  $bottom?: number;
}>`
  flex-direction: column;
  align-items: stretch;
  background-color: ${({ theme }) => theme.white};
  position: fixed;
  border-radius: ${Rounded.lg};
  width: 28rem;
  /* Anchored to the right edge by default, so on a narrow viewport cap it to
     what's left rather than letting it run off the other side. */
  max-width: calc(100vw - ${Spacing.m} * 2);
  height: 28rem;
  /* Same reasoning as max-width above, but for height — without this the
     panel can run off the bottom of a short viewport. */
  max-height: calc(100vh - ${Spacing.m} * 2);
  z-index: 1001;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: ${Shadow.SM};

  ${({ $left, $top, $bottom }) =>
    $left !== undefined
      ? css`
          left: ${$left}px;
          ${$top !== undefined
            ? css`
                top: ${$top}px;
              `
            : css`
                bottom: ${$bottom}px;
              `}
        `
      : css`
          bottom: calc(${Spacing.xxl} * 2.5);
          inset-inline-end: ${Spacing.m};
        `}
`;

type ChatWidgetPanelProps = {
  children: React.ReactNode;
  onBackAction: VoidFunction;
  current: ChatWidgetCurrent;
  width?: string;
  dataTestId?: string;
  buttonAnchor: { left: number; top: number } | null;
};

function getPanelPlacement(
  buttonAnchor: { left: number; top: number },
  vw: number,
  vh: number,
) {
  const effectiveWidth = Math.min(PANEL_SIZE_PX, vw - EDGE_GUTTER_PX * 2);
  const effectiveHeight = Math.min(PANEL_SIZE_PX, vh - EDGE_GUTTER_PX * 2);
  const buttonRight = buttonAnchor.left + FAHMY_BUTTON_SIZE_PX;
  const buttonBottom = buttonAnchor.top + FAHMY_BUTTON_SIZE_PX;

  // Horizontal: right-align to the button like the default corner does,
  // unless that would clip past the left edge — then left-align instead.
  const desiredLeft = buttonRight - effectiveWidth;
  const left = clamp(
    desiredLeft < EDGE_GUTTER_PX ? buttonAnchor.left : desiredLeft,
    EDGE_GUTTER_PX,
    vw - effectiveWidth - EDGE_GUTTER_PX,
  );

  // Vertical: open above the button (today's default relationship) when
  // there's room, or more room above than below; otherwise flip below it.
  const spaceAbove = buttonAnchor.top;
  const spaceBelow = vh - buttonBottom;
  const openAbove =
    spaceAbove >= effectiveHeight + GAP_PX || spaceAbove >= spaceBelow;
  const maxOffsetFromEdge = vh - effectiveHeight - EDGE_GUTTER_PX;

  if (openAbove) {
    const bottom = clamp(
      vh - buttonAnchor.top + GAP_PX,
      EDGE_GUTTER_PX,
      maxOffsetFromEdge,
    );
    return { left, bottom };
  }

  const top = clamp(
    buttonBottom + GAP_PX,
    EDGE_GUTTER_PX,
    maxOffsetFromEdge,
  );
  return { left, top };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export const ChatWidgetContentWrapper: React.FC<ChatWidgetPanelProps> = ({
  children,
  onBackAction,
  current,
  dataTestId,
  buttonAnchor,
}) => {
  const { width: vw, height: vh } = useWindowDimensions();

  const placement = buttonAnchor
    ? getPanelPlacement(buttonAnchor, vw, vh)
    : null;

  return (
    <Container
      dataTestId={dataTestId}
      $left={placement?.left}
      $top={placement?.top}
      $bottom={placement?.bottom}
    >
      <ChatWidgetMenuHeader onBack={onBackAction} current={current} />
      {children}
    </Container>
  );
};
